import type { PaginatedResponse } from "@nuxt/app";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  ReferralBenefitProjection,
  ReferralAttribution,
  ReferralLedgerEntry,
  ReferralListParams,
  ReferralOverview,
  ReferralPartnershipInterestResponse,
  ReferralWithdrawalRequest,
} from "~/types/referrals";

const EMPTY_BENEFIT_PROJECTION: ReferralBenefitProjection = {
  state: "none",
  next_maturity: null,
  next_charge: null,
};

const PAUSED_BENEFIT_PROJECTION: ReferralBenefitProjection = {
  ...EMPTY_BENEFIT_PROJECTION,
  state: "paused",
};

const AVAILABLE_BENEFIT_PROJECTION: ReferralBenefitProjection = {
  ...EMPTY_BENEFIT_PROJECTION,
  state: "available",
};

const MATURING_BENEFIT_PROJECTION: ReferralBenefitProjection = {
  ...EMPTY_BENEFIT_PROJECTION,
  state: "maturing",
};

type ReferralOverviewApiResponse = Omit<
  ReferralOverview,
  "benefit_projection"
> & {
  benefit_projection?: ReferralBenefitProjection;
};

function getLegacyBenefitProjection(
  overview: ReferralOverviewApiResponse,
): ReferralBenefitProjection {
  if (overview.subscription_credit_available_cents > 0) {
    return AVAILABLE_BENEFIT_PROJECTION;
  }

  if (
    overview.subscription_credit_pending_cents > 0 &&
    overview.subscription_credit_available_cents >= 0
  ) {
    return MATURING_BENEFIT_PROJECTION;
  }

  return overview.rewards_paused_reason
    ? PAUSED_BENEFIT_PROJECTION
    : EMPTY_BENEFIT_PROJECTION;
}

class ReferralsModule extends HttpFactory {
  async getOverview(): Promise<ReferralOverview> {
    const overview = await this.get<ReferralOverviewApiResponse>(
      ENDPOINTS.REFERRALS.OVERVIEW,
    );

    return {
      ...overview,
      benefit_projection:
        overview.benefit_projection ?? getLegacyBenefitProjection(overview),
    };
  }

  async getAttributions(
    params: ReferralListParams = {},
  ): Promise<PaginatedResponse<ReferralAttribution>> {
    return await this.get(ENDPOINTS.REFERRALS.ATTRIBUTIONS, params);
  }

  async getLedger(
    params: ReferralListParams = {},
  ): Promise<PaginatedResponse<ReferralLedgerEntry>> {
    return await this.get(ENDPOINTS.REFERRALS.LEDGER, params);
  }

  async getWithdrawalRequests(
    params: ReferralListParams = {},
  ): Promise<PaginatedResponse<ReferralWithdrawalRequest>> {
    return await this.get(ENDPOINTS.REFERRALS.WITHDRAWAL_REQUESTS, params);
  }

  async requestWithdrawal(): Promise<ReferralWithdrawalRequest> {
    return await this.post(ENDPOINTS.REFERRALS.WITHDRAWAL_REQUESTS, {});
  }

  async submitPartnershipInterest(): Promise<ReferralPartnershipInterestResponse> {
    return await this.post(ENDPOINTS.REFERRALS.PARTNERSHIP_INTEREST, {});
  }
}

export default ReferralsModule;
